import {
  fetchNotificationSettings,
  type ApplicationNotifyEvent,
  type ApplicationNotifyResult,
} from './applicationFormSettings';
import {
  fetchAccountCreationRoles,
  type AccountCreationRoleKind,
} from './accountCreationRoles';
import { fetchApplicationFormDepartments } from './applicationFormWorkflow';
import { formatEmailProxyError } from './demOrderNotifications';
import { emailToUsername } from './emailUtils';
import { isValidEmail } from './emailValidation';
import { formatDisplayPersonName } from './formatPersonName';
import { fetchUnifiedPortalUsers } from './portalUnifiedUsers';
import { getDbSupabase } from './supabase';
import type { AccountCreationFormData, ApplicationSubmission } from '../types/applicationForm';

const ACCOUNT_CREATION_IT_FINANCE_DEPT_SLUGS = new Set(['it', 'finance']);
const ACCOUNT_CREATION_IT_DEPT_SLUGS = new Set(['it']);

export type NotifyRecipientLabel = { email: string; name: string };

/** Portal department members for Account Creation notify (default: IT + Finance). */
export async function resolveAccountCreationDeptNotifyRecipients(
  deptSlugs: Set<string> = ACCOUNT_CREATION_IT_FINANCE_DEPT_SLUGS,
): Promise<NotifyRecipientLabel[]> {
  const [users, departments] = await Promise.all([
    fetchUnifiedPortalUsers(),
    fetchApplicationFormDepartments(),
  ]);
  const deptIds = new Set(
    departments
      .filter((dept) => deptSlugs.has(dept.slug.trim().toLowerCase()))
      .map((dept) => dept.id),
  );
  const out: NotifyRecipientLabel[] = [];
  const seen = new Set<string>();
  for (const user of users) {
    if (user.removedAt) continue;
    const deptId = user.departmentId ?? user.forms.department_id ?? '';
    if (!deptId || !deptIds.has(deptId)) continue;
    const email = user.email.trim();
    const key = email.toLowerCase();
    if (!isValidEmail(email) || seen.has(key)) continue;
    seen.add(key);
    out.push({
      email,
      name:
        formatDisplayPersonName(user.displayName) ||
        formatDisplayPersonName(emailToUsername(email)) ||
        email,
    });
  }
  return out;
}

/** Emails from Account Creation workflow roles (verifier / approver). */
export async function resolveAccountCreationRoleEmails(
  role: AccountCreationRoleKind,
): Promise<string[]> {
  try {
    const roles = await fetchAccountCreationRoles();
    return uniqueValidEmails(
      roles.filter((row) => row.role === role).map((row) => row.email),
    );
  } catch {
    return [];
  }
}

export function formatNotifyRecipientNames(
  emails: string[],
  labels: NotifyRecipientLabel[],
): string {
  const byEmail = new Map(labels.map((row) => [row.email.toLowerCase(), row.name]));
  const names: string[] = [];
  const seen = new Set<string>();
  for (const email of emails) {
    const key = email.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    names.push(
      byEmail.get(key) ||
        formatDisplayPersonName(emailToUsername(email)) ||
        email.trim(),
    );
  }
  return names.join(', ');
}

function applyTemplate(template: string, vars: Record<string, string>): string {
  let out = template;
  for (const [key, value] of Object.entries(vars)) {
    out = out.split(`{${key}}`).join(value);
  }
  return out;
}

function toHtml(body: string): string {
  const escaped = body
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<html><body><div style="font-family:'Segoe UI',Arial,sans-serif;font-size:14px;color:#333;line-height:1.5;">${escaped.replace(/\n/g, '<br>')}</div></body></html>`;
}

function uniqueValidEmails(emails: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const email of emails) {
    const trimmed = email.trim();
    const key = trimmed.toLowerCase();
    if (!isValidEmail(trimmed) || seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

function buildTemplateVars(submission: ApplicationSubmission): Record<string, string> {
  const formData = submission.form_data as AccountCreationFormData;
  const financeData = submission.finance_data;
  const salespersonCode =
    financeData?.salesperson_code?.trim() || formData.salesperson_code?.trim() || '';
  return {
    application_no: submission.application_no,
    company_name: submission.company_name,
    source: submission.source === 'internal' ? 'Internal' : 'Customer portal',
    submitted_by: submission.submitted_by?.trim() || '—',
    customer_code: submission.customer_code?.trim() || '—',
    review_notes: submission.review_notes?.trim() || '—',
    salesperson_code: salespersonCode || '—',
    delivery_schedule: financeData?.delivery_schedule?.trim() || '—',
  };
}

async function resolveSalesEmails(salespersonCode?: string): Promise<string[]> {
  const code = salespersonCode?.trim();
  if (!code) return [];
  try {
    const db = getDbSupabase('forms');
    const { data, error } = await db.rpc('resolve_salesperson_notify_emails', {
      p_code: code,
    });
    if (!error && Array.isArray(data)) {
      return uniqueValidEmails(data.map((v) => String(v ?? '')));
    }
  } catch {
    /* no salesperson recipients */
  }
  return [];
}

export function canSendApplicationFormEmailViaPortal(): boolean {
  return Boolean(import.meta.env.VITE_DEM_EMAIL_PROXY_KEY?.trim());
}

async function sendViaPortalProxy(to: string[], subject: string, html: string): Promise<void> {
  const proxyKey = import.meta.env.VITE_DEM_EMAIL_PROXY_KEY?.trim();
  if (!proxyKey) {
    throw new Error('VITE_DEM_EMAIL_PROXY_KEY is not configured in this portal build.');
  }

  const res = await fetch('/order-email-api', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-DEM-Email-Key': proxyKey,
    },
    body: JSON.stringify({ to, subject, html }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(formatEmailProxyError(text, res.status));
  }

  const payload = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
  if (payload.ok === false) {
    throw new Error(payload.error || 'Order email proxy rejected the send request.');
  }
}

export async function sendApplicationFormTestEmailViaPortal(
  toEmail?: string | null,
): Promise<ApplicationNotifyResult> {
  const target = String(toEmail ?? '').trim();
  if (!isValidEmail(target)) {
    return { ok: false, error: 'Sign in with a valid work email to send a test message.' };
  }

  try {
    await sendViaPortalProxy(
      [target],
      'BTH Application Form — test email',
      toHtml(
        'This is a test message sent via the portal order-email proxy. If you received this, application form email is working.',
      ),
    );
    return { ok: true, sent: [{ kind: 'test', to: [target] }] };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : 'Test email failed.' };
  }
}

export async function sendApplicationFormEventViaPortal(
  submission: ApplicationSubmission,
  event: ApplicationNotifyEvent,
): Promise<ApplicationNotifyResult> {
  const settings = await fetchNotificationSettings();
  // Notifications stay on by default; Settings no longer exposes an off switch.

  const vars = buildTemplateVars(submission);
  const formData = submission.form_data as AccountCreationFormData;
  const salesCode = formData.salesperson_code?.trim() || submission.finance_data?.salesperson_code?.trim();
  const sent: NonNullable<ApplicationNotifyResult['sent']> = [];
  const skipped: NonNullable<ApplicationNotifyResult['skipped']> = [];

  async function trySend(kind: string, recipients: string[], subjectTemplate: string, bodyTemplate: string) {
    const to = uniqueValidEmails(recipients);
    if (to.length === 0) {
      skipped.push({ kind, reason: 'No valid recipient email' });
      return;
    }
    try {
      const subject = applyTemplate(subjectTemplate, vars);
      const body = applyTemplate(bodyTemplate, vars);
      await sendViaPortalProxy(to, subject, toHtml(body));
      sent.push({ kind, to });
    } catch (error) {
      skipped.push({
        kind,
        reason: error instanceof Error ? error.message : 'Send failed',
      });
    }
  }

  if (event === 'submission') {
    // Public customer copy only for customer-portal submissions.
    if (submission.source === 'public') {
      await trySend(
        'customer',
        [formData.finance_email, formData.sales_email],
        settings.submission_customer_subject,
        settings.submission_customer_body,
      );
    }

    // Sales + verifiers + IT (finance/AR wait until Confirm verification).
    const salesEmails = await resolveSalesEmails(salesCode);
    const verifierEmails = await resolveAccountCreationRoleEmails('verifier');
    const itRecipients = await resolveAccountCreationDeptNotifyRecipients(
      ACCOUNT_CREATION_IT_DEPT_SLUGS,
    );
    const salesRecipients = uniqueValidEmails([
      ...salesEmails,
      ...verifierEmails,
      ...itRecipients.map((row) => row.email),
    ]);
    if (salesRecipients.length > 0) {
      await trySend(
        'sales',
        salesRecipients,
        settings.submission_sales_subject,
        settings.submission_sales_body,
      );
    } else {
      skipped.push({
        kind: 'sales',
        reason: salesCode
          ? `No salesperson, verifier, or IT emails for code "${salesCode}"`
          : 'No salesperson, verifier, or IT recipients',
      });
    }
    if (submission.source === 'internal') {
      await trySend(
        'internal',
        [submission.submitter_email ?? ''],
        settings.submission_internal_subject,
        settings.submission_internal_body,
      );
    }
  }

  if (event === 'verified') {
    // After Confirm verification → Finance/IT + Approvers (AR).
    try {
      const deptRecipients = await resolveAccountCreationDeptNotifyRecipients();
      const approverEmails = await resolveAccountCreationRoleEmails('approver');
      const financeRecipients = uniqueValidEmails([
        ...deptRecipients.map((row) => row.email),
        ...approverEmails,
      ]);
      if (financeRecipients.length > 0) {
        await trySend(
          'finance',
          financeRecipients,
          settings.submission_finance_subject,
          settings.submission_finance_body,
        );
      } else {
        skipped.push({
          kind: 'finance',
          reason: 'No IT/Finance department or Approver recipients found',
        });
      }
    } catch (error) {
      skipped.push({
        kind: 'finance',
        reason: error instanceof Error ? error.message : 'Failed to resolve finance/approver recipients',
      });
    }
  }

  if (event === 'approved' || event === 'rejected') {
    // One email: IT/Finance + approvers + salesperson + verifiers + internal submitter.
    // Customer emails paused for now.
    const subjectTemplate =
      event === 'approved'
        ? settings.approved_internal_subject || settings.submission_finance_subject
        : settings.rejected_internal_subject || settings.submission_finance_subject;
    const bodyTemplate =
      event === 'approved'
        ? settings.approved_internal_body || settings.submission_finance_body
        : settings.rejected_internal_body || settings.submission_finance_body;

    try {
      const deptRecipients = await resolveAccountCreationDeptNotifyRecipients();
      const approverEmails = await resolveAccountCreationRoleEmails('approver');
      const verifierEmails = await resolveAccountCreationRoleEmails('verifier');
      const salesEmails = await resolveSalesEmails(salesCode);
      const decisionRecipients = uniqueValidEmails([
        ...deptRecipients.map((row) => row.email),
        ...approverEmails,
        ...salesEmails,
        ...verifierEmails,
        ...(submission.source === 'internal' ? [submission.submitter_email ?? ''] : []),
      ]);
      if (decisionRecipients.length > 0) {
        await trySend(
          'decision',
          decisionRecipients,
          subjectTemplate,
          bodyTemplate,
        );
      } else {
        skipped.push({
          kind: 'decision',
          reason:
            'No IT/Finance, approver, salesperson, verifier, or submitter recipients found',
        });
      }
    } catch (error) {
      skipped.push({
        kind: 'decision',
        reason:
          error instanceof Error
            ? error.message
            : 'Failed to resolve approve/reject recipients',
      });
    }
  }

  const allEmails = uniqueValidEmails(sent.flatMap((item) => item.to));
  let recipientLabels: NotifyRecipientLabel[] = [];
  try {
    const users = await fetchUnifiedPortalUsers();
    const byEmail = new Map(
      users.map((user) => [
        user.email.trim().toLowerCase(),
        formatDisplayPersonName(user.displayName) ||
          formatDisplayPersonName(emailToUsername(user.email)) ||
          user.email,
      ]),
    );
    recipientLabels = allEmails.map((email) => ({
      email,
      name: byEmail.get(email.toLowerCase()) || formatDisplayPersonName(emailToUsername(email)) || email,
    }));
  } catch {
    recipientLabels = allEmails.map((email) => ({
      email,
      name: formatDisplayPersonName(emailToUsername(email)) || email,
    }));
  }

  return {
    ok: sent.length > 0,
    sent,
    skipped,
    recipientLabels,
    error: sent.length === 0 ? 'No emails were sent. See skipped reasons.' : undefined,
  };
}
